import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ExternalLink, Minus, Plus, RotateCcw, ShieldCheck, ShoppingBag, Star, Truck } from "lucide-react";
import { toast } from "sonner";
import { api, fmt } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useCustomer } from "../context/CustomerContext";
import { ProductCard } from "../components/ProductCard";

export default function ProductPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [qty, setQty] = useState(1);
  const { add } = useCart();
  const { customer } = useCustomer();

  useEffect(() => {
    setData(null);
    api.get(`/products/${id}`).then((r) => setData(r.data)).catch(() => {});
  }, [id]);

  useEffect(() => {
    if (customer) api.post("/track/view", { product_id: id }).catch(() => {});
  }, [id, customer]);

  if (!data)
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-amber-400 border-t-transparent" />
      </div>
    );

  const { product: p, supplier, related } = data;

  return (
    <div className="mx-auto max-w-7xl px-4 py-12 sm:px-8" data-testid="product-page">
      <div className="grid gap-10 lg:grid-cols-2">
        <div className="gold-glow overflow-hidden rounded-3xl border border-white/10">
          <img src={p.images[0]} alt={p.name} className="aspect-square w-full object-cover" data-testid="product-image" />
        </div>
        <div>
          <p className="eyebrow">{p.category} · {p.niche}</p>
          <h1 className="mt-2 font-heading text-3xl font-bold text-white sm:text-4xl" data-testid="product-name">{p.name}</h1>
          <div className="mt-3 flex items-center gap-2 text-sm text-slate-400">
            <Star size={15} className="fill-amber-400 text-amber-400" />
            <span className="font-semibold text-white">{p.rating}</span> ({p.reviews} reviews)
            <span className="text-slate-600">·</span>
            <span className="text-emerald-400">{p.sold_count.toLocaleString()} sold</span>
          </div>
          <div className="mt-5 flex items-baseline gap-3">
            <span className="font-heading text-4xl font-bold text-white" data-testid="product-price">{fmt(p.price)}</span>
            <span className="text-lg text-slate-600 line-through">{fmt(p.price * 1.45)}</span>
          </div>
          <p className="mt-5 leading-relaxed text-slate-400">{p.description}</p>

          <div className="mt-5 flex items-center gap-2 text-sm">
            <Truck size={15} className="text-emerald-400" />
            <span className="text-emerald-400" data-testid="product-stock">
              In stock — ships direct from supplier in 7–15 days
            </span>
            <span className="text-slate-600">({p.inventory} units allocated)</span>
          </div>

          {!p.returnable && (
            <div className="mt-4 rounded-xl border border-rose-400/30 bg-rose-400/10 px-4 py-3 text-sm text-rose-300" data-testid="non-returnable-note">
              Non-returnable item ({p.non_returnable_reason || "final sale"}).
            </div>
          )}
          {p.returnable && (
            <div className="mt-4 flex items-center gap-2 text-sm text-slate-400">
              <RotateCcw size={15} className="text-amber-400" /> 30-day returns · unused & original packaging
            </div>
          )}

          <div className="mt-7 flex items-center gap-4">
            <div className="flex items-center rounded-full border border-white/15">
              <button data-testid="qty-decrease" onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-4 py-3 text-slate-300 hover:text-white"><Minus size={15} /></button>
              <span className="w-8 text-center font-semibold text-white" data-testid="qty-value">{qty}</span>
              <button data-testid="qty-increase" onClick={() => setQty((q) => Math.min(20, q + 1))} className="px-4 py-3 text-slate-300 hover:text-white"><Plus size={15} /></button>
            </div>
            <button
              data-testid="add-to-cart-btn"
              onClick={() => { add(p, qty); toast.success(`${p.name} added to cart`); }}
              className="btn-gold flex-1"
            >
              <ShoppingBag size={17} /> Add to cart
            </button>
          </div>

          {supplier && (
            <div className="card-surface mt-8 p-5" data-testid="supplier-card">
              <p className="eyebrow mb-3">Ships direct from manufacturer</p>
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="font-semibold text-white">{supplier.name}</p>
                  <p className="mt-1 text-xs text-slate-400">
                    {supplier.location} · {supplier.rating}★ · {supplier.on_time_rate}% on-time · replies {supplier.response_time}
                  </p>
                </div>
                <a
                  href={supplier.url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="supplier-link"
                  className={`flex shrink-0 items-center gap-1.5 rounded-full border px-4 py-2 text-xs font-semibold transition-colors ${
                    supplier.platform === "alibaba"
                      ? "border-orange-400/40 text-orange-300 hover:bg-orange-400/10"
                      : "border-rose-400/40 text-rose-300 hover:bg-rose-400/10"
                  }`}
                >
                  {supplier.platform === "alibaba" ? "Alibaba" : "AliExpress"} <ExternalLink size={12} />
                </a>
              </div>
            </div>
          )}

          <div className="mt-5 flex items-center gap-2 text-xs text-slate-500">
            <ShieldCheck size={14} className="text-amber-400" /> Secure Stripe checkout · also listed on
            {p.channels?.ebay?.listed && <span className="font-semibold text-sky-400"> eBay</span>}
            {p.channels?.tiktok?.listed && <span className="font-semibold text-rose-400"> TikTok Shop</span>}
          </div>
        </div>
      </div>

      {related?.length > 0 && (
        <section className="mt-16">
          <h2 className="mb-6 font-heading text-2xl font-semibold text-white">You may also like</h2>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            {related.map((r) => (
              <ProductCard key={r.id} product={r} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
